import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { X, HeartHandshake, Wallet, Users, Search, Check, Clock, XCircle, HelpCircle, Send } from 'lucide-react';
import api from '../../../utils/api';

// The Email calendar's extra fields: the file an appointment is linked to (an SC participant, a PM
// participant or a client; they stay separate and say which they are), the people invited, and
// the mailbox the invites go from.
export const LINK_KINDS = {
  sc: { label: 'Support Coordination', short: 'SC', icon: HeartHandshake, chip: 'bg-violet-50 text-violet-700 ring-violet-200' },
  pm: { label: 'Plan Management', short: 'PM', icon: Wallet, chip: 'bg-teal-50 text-teal-700 ring-teal-200' },
  care: { label: 'Client', short: 'Client', icon: Users, chip: 'bg-amber-50 text-amber-800 ring-amber-200' },
};
export const fileHrefOf = (link) => (!link ? null : link.module === 'sc' ? `/support-coordination/${link.id}`
  : link.module === 'pm' ? `/plan-management/participants/${link.client_id || link.id}` : `/clients/${link.client_id || link.id}`);

const ctl = 'w-full rounded-xl bg-white ring-1 ring-inset ring-slate-300 text-[14px] text-slate-900 placeholder:text-slate-400 focus-within:ring-2 focus-within:ring-indigo-500';

export function LinkPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(0);
  const seq = useRef(0);
  useEffect(() => {
    if (!open) return undefined;
    const my = ++seq.current;
    const t = setTimeout(() => {
      api.get('/calendar/link-search', { params: { q } }).then((r) => { if (my === seq.current) { setRows(r.data || []); setActive(0); } }).catch(() => { if (my === seq.current) setRows([]); });
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);
  if (value) {
    const K = LINK_KINDS[value.module] || LINK_KINDS.care;
    return (
      <div className="flex items-center gap-2">
        <span className={clsx('inline-flex items-center gap-1.5 h-9 pl-2.5 pr-1 rounded-xl ring-1 ring-inset text-[13px] font-semibold', K.chip)}>
          <K.icon size={14} aria-hidden />{value.name}<span className="opacity-70 font-medium">· {K.short}</span>
          <button type="button" onClick={() => onChange(null)} aria-label="Remove the link" className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5"><X size={13} aria-hidden /></button>
        </span>
      </div>
    );
  }
  const pick = (r) => { onChange({ module: r.module, id: r.id, client_id: r.client_id, name: r.label }); setOpen(false); setQ(''); };
  return (
    <div className="relative">
      <div className={clsx(ctl, 'flex items-center gap-2 h-11 sm:h-10 px-3')}>
        <Search size={15} className="text-slate-400 flex-shrink-0" aria-hidden />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(rows.length - 1, a + 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
            else if (e.key === 'Enter' && rows[active]) { e.preventDefault(); pick(rows[active]); }
            else if (e.key === 'Escape') setOpen(false);
          }}
          placeholder="Search a participant or client" aria-label="Link to a participant or client" role="combobox" aria-expanded={open} aria-autocomplete="list"
          className="flex-1 min-w-0 bg-transparent outline-none text-[14px]" />
      </div>
      {open && rows.length > 0 && (
        <ul role="listbox" className="absolute z-[95] mt-1 w-full max-h-64 overflow-y-auto bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-1">
          {rows.map((r, i) => {
            const K = LINK_KINDS[r.module] || LINK_KINDS.care;
            return (
              <li key={`${r.module}:${r.id}`} role="option" aria-selected={i === active}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(r)}
                  className={clsx('w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-[13px]', i === active ? 'bg-indigo-50' : 'hover:bg-slate-50')}>
                  <K.icon size={14} className="text-slate-500" aria-hidden />
                  <span className="flex-1 truncate text-slate-800">{r.label}</span>
                  <span className={clsx('px-1.5 py-px rounded text-[10.5px] font-semibold ring-1', K.chip)}>{K.short}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const STATUS = {
  accepted: { icon: Check, cls: 'text-emerald-600', label: 'Accepted' },
  declined: { icon: XCircle, cls: 'text-rose-600', label: 'Declined' },
  tentative: { icon: HelpCircle, cls: 'text-amber-600', label: 'Maybe' },
  'needs-action': { icon: Clock, cls: 'text-slate-400', label: 'No answer yet' },
};
export const attendeeStatus = (s) => STATUS[s] || STATUS['needs-action'];
const EMAIL = /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[a-z]{2,}$/i;

// People to invite: type an address or a name (suggestions come from your mail and your
// participants' contacts); Enter, comma or Tab adds it.
export function AttendeesField({ value = [], onChange }) {
  const [text, setText] = useState('');
  const [sugg, setSugg] = useState([]);
  const [err, setErr] = useState(null);
  const seq = useRef(0);
  useEffect(() => {
    const t = text.trim();
    if (t.length < 2) { setSugg([]); return undefined; }
    const my = ++seq.current;
    const h = setTimeout(() => api.get('/mail/suggest', { params: { q: t } }).then((r) => { if (my === seq.current) setSugg((r.data || []).slice(0, 6)); }).catch(() => {}), 200);
    return () => clearTimeout(h);
  }, [text]);
  const add = (a) => {
    const email = String(a.address || a.email || '').trim().toLowerCase();
    if (!EMAIL.test(email)) { setErr(`${email || text} is not an email address.`); return; }
    setErr(null);
    if (!value.some((x) => x.email === email)) onChange([...value, { email, name: a.name || '', status: 'needs-action' }]);
    setText(''); setSugg([]);
  };
  const parseTyped = () => {
    const t = text.trim().replace(/[,;]$/, '');
    if (!t) return;
    const m = /^(.*)<([^>]+)>$/.exec(t);
    add(m ? { name: m[1].trim().replace(/^"|"$/g, ''), address: m[2] } : { address: t });
  };
  return (
    <div>
      <div className={clsx(ctl, 'flex flex-wrap items-center gap-1.5 min-h-[44px] sm:min-h-[40px] px-2 py-1.5')}>
        {value.map((a) => {
          const S = attendeeStatus(a.status);
          return (
            <span key={a.email} className="inline-flex items-center gap-1 h-7 pl-2 pr-0.5 rounded-lg bg-slate-100 text-[12.5px] text-slate-800" title={`${a.email} · ${S.label}`}>
              <S.icon size={12} className={S.cls} aria-label={S.label} />
              <span className="max-w-[180px] truncate">{a.name || a.email}</span>
              <button type="button" onClick={() => onChange(value.filter((x) => x.email !== a.email))} aria-label={`Remove ${a.email}`} className="w-6 h-6 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-200"><X size={12} aria-hidden /></button>
            </span>
          );
        })}
        <input value={text} onChange={(e) => { setText(e.target.value); setErr(null); }}
          onKeyDown={(e) => {
            if (['Enter', ',', ';', 'Tab'].includes(e.key) && text.trim()) { e.preventDefault(); parseTyped(); }
            else if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => { if (text.trim() && EMAIL.test(text.trim())) parseTyped(); }}
          placeholder={value.length ? 'Add another' : 'Name or email address'} aria-label="Invite people"
          className="flex-1 min-w-[140px] h-7 bg-transparent outline-none text-[14px] px-1" />
      </div>
      {sugg.length > 0 && (
        <ul className="mt-1 rounded-xl ring-1 ring-slate-200 bg-white p-1 shadow-sm">
          {sugg.map((s) => (
            <li key={s.address}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(s)} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-[13px] hover:bg-slate-50">
                <span className="flex-1 truncate text-slate-800">{s.name || s.address}</span><span className="text-[12px] text-slate-500 truncate">{s.address}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {err && <p className="mt-1 text-[12.5px] text-rose-600" role="alert">{err}</p>}
    </div>
  );
}

// The mailboxes this person can send from (their own, or a shared one).
export function useMyMailboxes(enabled = true) {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    api.get('/mail/accounts').then((r) => setList((r.data || []).filter((a) => a.can_act))).catch(() => setList([]));
  }, [enabled]);
  return list;
}
export function OrganizerSelect({ value, onChange, mailboxes }) {
  const opts = useMemo(() => (mailboxes || []).map((a) => ({ value: a.id, label: a.email })), [mailboxes]);
  if (!opts.length) return <p className="text-[12.5px] text-slate-600 bg-slate-50 rounded-lg px-3 py-2">Connect your mailbox in Email to send invites. You can still add people now; the invites go once a mailbox is chosen.</p>;
  return (
    <label className="flex items-center gap-2 text-[13px] text-slate-700">
      <Send size={14} className="text-slate-400" aria-hidden />
      <span className="flex-shrink-0">Send invites from</span>
      <select value={value || ''} onChange={(e) => onChange(e.target.value || null)} className="flex-1 min-w-0 h-9 rounded-lg ring-1 ring-inset ring-slate-300 bg-white px-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500">
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
